import { uploadMedia } from "../../services/mediaLibraryService";

const nullSafeText = (value) => (value == null ? "" : String(value));

export const extractApiMessage = (err) => {
  const data = err?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.join(" ");
  }
  return data?.message ?? data?.Message ?? "";
};

export const buildCropFileName = (name) => {
  const base =
    String(name || "image")
      .trim()
      .replace(/[^\w\s.-]/g, "")
      .replace(/\s+/g, "-") || "image";
  return base.includes(".") ? base : `${base}.png`;
};

const extractUploaded = (res) => {
  const data = res?.data;
  return data?.result ?? data?.Result ?? data?.data ?? data ?? null;
};

const normalizeUploaded = (raw, fallback, meta) => {
  if (!raw) return null;
  return {
    mediaAssetId:
      raw.mediaAssetId ?? raw.id ?? raw._id ?? raw.mediaId ?? raw.assetId,
    mediaUrl: raw.mediaUrl ?? raw.url ?? raw.fileUrl ?? raw.path ?? "",
    thumbnailUrl:
      raw.thumbnailUrl ??
      raw.thumbUrl ??
      raw.thumbnail ??
      raw.mediaUrl ??
      raw.url ??
      "",
    mediaName:
      nullSafeText(raw.mediaName ?? raw.name ?? raw.fileName ?? raw.title) ||
      fallback?.mediaName ||
      "Untitled",
    providerType:
      raw.providerType ?? raw.provider ?? fallback?.providerType ?? "",
    width: meta?.width ?? raw.width ?? fallback?.width ?? null,
    height: meta?.height ?? raw.height ?? fallback?.height ?? null,
  };
};

/** Uploads a cropped external import and returns the normalized media asset. */
export const uploadCroppedImport = async (
  file,
  cropFileName,
  pendingImport,
  meta
) => {
  const formData = new FormData();
  formData.append("MediaFile", file, cropFileName);
  const uploadRes = await uploadMedia(formData);
  return normalizeUploaded(extractUploaded(uploadRes), pendingImport, meta);
};

/** Cropper source priority: full image first, then preview, then thumbnail. */
export const resolveCropImageUrl = (media = {}, fallback = {}) => {
  const candidates = [
    media.mediaUrl,
    media.previewUrl,
    media.thumbnailUrl,
    fallback.mediaUrl,
    fallback.previewUrl,
    fallback.imageUrl,
    fallback.thumbnailUrl,
  ];

  for (const candidate of candidates) {
    const value = candidate == null ? "" : String(candidate).trim();
    if (value) return value;
  }

  return "";
};

/**
 * External-import crop flow loads images via browser fetch() before CropperJS.
 *
 * Handing a remote URL straight to <Cropper src> would either fail to load
 * (network/CORS block, showing a blank stage with no error surfaced) or,
 * even if it displays, taint the canvas so getCroppedCanvas()/toBlob() can't
 * read pixel data — the crop step would silently fail. Fetching the bytes
 * ourselves and wrapping them in a local blob: URL sidesteps both problems:
 * a blob: URL created in this document is always canvas-safe, and a failed
 * fetch throws here instead of leaving the cropper blank.
 *
 * AWS S3 bucket CORS is required — without Access-Control-Allow-Origin the
 * fetch fails before Cropper receives any pixels. This is a storage/network
 * issue, not a CropperJS defect.
 *
 * Dev example origin: http://localhost:3000
 *
 * Example S3 bucket CORS configuration:
 * [
 *   {
 *     "AllowedOrigins": ["http://localhost:3000"],
 *     "AllowedMethods": ["GET", "HEAD"],
 *     "AllowedHeaders": ["*"]
 *   }
 * ]
 */
export const CROP_IMAGE_CORS_ERROR =
  "Image cannot be loaded because the storage server does not allow browser access (CORS).";

/**
 * Resolves the Cropper image source.
 * Local blob:/data: URLs (Upload Device flow) are already same-origin and
 * owned by the caller, so they pass through unchanged. Remote URLs (external
 * provider imports, stored media) are fetched and converted to a local blob:
 * URL before being handed to CropperJS.
 */
export const prepareCropImageSrc = async (url) => {
  const src = String(url || "").trim();
  if (!src) {
    throw new Error("Empty image URL");
  }

  if (src.startsWith("blob:") || src.startsWith("data:")) {
    return { src, revoke: null };
  }

  let response;
  try {
    response = await fetch(src, { mode: "cors", credentials: "omit" });
  } catch {
    throw new Error(CROP_IMAGE_CORS_ERROR);
  }

  if (!response.ok) {
    throw new Error(CROP_IMAGE_CORS_ERROR);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  return {
    src: objectUrl,
    revoke: () => URL.revokeObjectURL(objectUrl),
  };
};
